import { FieldArrayWithId } from "react-hook-form";
import { TFunction } from "react-i18next";
import { focusBarcodeScannerInput } from "../../../../../../../components/Product/BarcodeScanner/utils";
import {
	BundleApi,
	BundleItemApi,
	OrderApi,
	OrderItemApi,
	ProductApi,
	PurchaseApi,
	PurchaseItemApi,
	StockAdjustmentApi,
	StockAdjustmentItemApi,
	TransferApi,
	TransferItemApi,
} from "../../../../../../../services/Api/Organization/types";

type FieldType = OrderApi | TransferApi | StockAdjustmentApi | PurchaseApi | BundleApi;

export const isItemRowIndexShown = (shownIndexes: number[], index: number) => shownIndexes.includes(index);

const getFieldsWithSelectedProduct = (fields: FieldArrayWithId<FieldType, "items", "key">[]) =>
	fields.filter((field) => field?.product?.id);

export const areAllItemsRowsIndexesShown = (
	fields: FieldArrayWithId<FieldType, "items", "key">[],
	shownIndexes: number[]
) => {
	return getFieldsWithSelectedProduct(fields).every((item, index) => {
		return shownIndexes.includes(index);
	});
};

export const showMore = (shownIndexes: number[], setShownIndexes: (newIndexes: number[]) => void, index: number) => {
	if (isItemRowIndexShown(shownIndexes, index)) {
		const indexes = [...shownIndexes];
		indexes.splice(shownIndexes.indexOf(index), 1);
		setShownIndexes([...indexes]);
	} else {
		setShownIndexes([...shownIndexes, index]);
	}
};

export const toggleAllItemsRows = (
	shownIndexes: number[],
	watchedItems: (OrderItemApi | TransferItemApi | StockAdjustmentItemApi | PurchaseItemApi | BundleItemApi)[],
	setShownIndexes: (newIndexes: number[]) => void,
	fields: FieldArrayWithId<FieldType, "items", "key">[]
) => {
	const parsedFields = getFieldsWithSelectedProduct(fields);
	if (!watchedItems?.some((item) => item?.product?.id)) return;
	if (areAllItemsRowsIndexesShown(fields, shownIndexes) || !parsedFields) {
		setShownIndexes([]);
	} else {
		setShownIndexes([...parsedFields.map((_, i) => i)]);
	}
};

export const handleTurnOnBarcodeScanner = (
	showBarcodeScanner: boolean,
	setShowBarcodeScanner: (value: boolean) => void
) => {
	if (!showBarcodeScanner) setShowBarcodeScanner(true);
	else {
		focusBarcodeScannerInput();
	}
};

export const drawProductTotalVolume = (
	item: TransferItemApi | OrderItemApi | StockAdjustmentItemApi,
	t: TFunction,
	negativeAmount?: boolean
) => {
	const unit = item?.product?.measure_type;
	let amount: number;
	if (!item.volume || item.volume === 0) amount = 0;
	else if (!item.quantity || item.quantity === 0) amount = 0;
	else {
		amount = (item.volume || 0) * (item.quantity || 0);
	}

	if (negativeAmount && item.quantity !== null && item.quantity < 0) amount *= -1;
	return `${amount.toFixed(5)} ${unit ? t(`enums.products.measure_types.${item?.product?.measure_type}`) : ""}`;
};

export const getProductVolumeLabel = (
	product: ProductApi,
	item: TransferItemApi | OrderItemApi | StockAdjustmentItemApi | PurchaseItemApi | BundleItemApi
) => {
	if (!product.product_volumes || product.product_volumes.length === 0) return "";

	const productVolume = product.product_volumes.find(
		(productVolume) => Number(productVolume.volume) === Number(item.volume)
	);
	if (!productVolume) return "";

	let label = " ";
	if (productVolume.volume !== null && productVolume.volume !== undefined) label += productVolume.volume;
	if (productVolume.label !== null && productVolume.label !== undefined) label += ` (${productVolume.label})`;

	return label;
};
